'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { clearPendingAuthAction, savePendingAuthAction, type ResumeAction } from '../lib/pendingAuthAction';
export type DialogOptionsProduct = {
  productName: string;
  variantSummary: string;
  imageUrl: string;
};
export type DialogOptionsBenefit = {
  icon: ReactNode;
  title: string;
  description: string;
};
export type DialogOptions = {
  title: string;
  icon: ReactNode;
  // When set, the sheet shows this material card instead of the generic icon
  // header (e.g. the add-to-cart flow). Omitted for product-less contexts.
  product?: DialogOptionsProduct;
  description?: string;
  // When set, the sheet leads with what signing in unlocks and can be dismissed
  // without signing in (the prompt shown when a guest lands on a tag).
  benefits?: DialogOptionsBenefit[];
  // When set, the sheet opens straight onto the email field under the icon,
  // title and description, with no Log in / Create an account choice first. An
  // email with no account still reaches sign-up after its code.
  startWithEmail?: boolean;
  // What the guest tapped, carried on once they sign in. Opening without one
  // drops any earlier action, so it can't fire after an unrelated sign-in.
  resumeAction?: ResumeAction;
};
type AuthenticationDialogContextType = {
  isOpen: boolean;
  dialogOptions: DialogOptions;
  open: (options: DialogOptions) => void;
  // Opens only if no sheet is open, including one opened earlier in this same
  // commit, which `isOpen` can't see yet. For prompts nobody asked for.
  openIfIdle: (options: DialogOptions) => void;
  close: () => void;
};
const defaultDialogOptions: DialogOptions = {
  title: '',
  icon: null
};
const AuthenticationDialogContext = createContext<AuthenticationDialogContextType>({} as AuthenticationDialogContextType);
export function AuthenticationDialogProvider(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState(defaultDialogOptions);
  const isOpenRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = options => {
      isOpenRef.current = true;
      clearPendingAuthAction();
      if (options.resumeAction) {
        savePendingAuthAction(options.resumeAction);
      }
      setDialogOptions(options);
      setIsOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const open = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = options_0 => {
      if (isOpenRef.current) {
        return;
      }
      open(options_0);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openIfIdle = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      isOpenRef.current = false;
      setIsOpen(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const close = t3;
  let t4;
  if ($[3] !== dialogOptions || $[4] !== isOpen) {
    t4 = {
      isOpen,
      dialogOptions,
      open,
      openIfIdle,
      close
    };
    $[3] = dialogOptions;
    $[4] = isOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const value = t4;
  let t5;
  if ($[6] !== children || $[7] !== value) {
    t5 = <AuthenticationDialogContext.Provider value={value}>{children}</AuthenticationDialogContext.Provider>;
    $[6] = children;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export function useAuthenticationDialog() {
  const context = useContext(AuthenticationDialogContext);
  if (!context) {
    throw new Error("useAuthenticationDialog must be used within a AuthenticationDialogProvider");
  }
  return context;
}